import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { AdminRole } from '@/lib/api-admin/types';
import type { AdminMemberDetail } from './useAdminUsersQuery';

/**
 * 회원 CS 정보 수정 · 비밀번호 재설정 인증번호 대행 발송(be-user feat/admin-member-detail-extra fc1f070).
 * 권한은 둘 다 SUPER_ADMIN · OPS_ADMIN · CS_AGENT. Idempotency-Key 는 필요 없다(다른 회원 관리 write 와 같음).
 */
export const MEMBER_CS_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT'];

/** BE 오류 — code(예: USER_EMAIL_ALREADY_EXISTS), message, 필드별 사유(details: { 필드명: 사유 }). */
export class MemberCsError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly fieldErrors: Readonly<Record<string, string>>,
  ) {
    super(message);
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: Record<string, unknown> | null } | null;
}

const toMemberCsError = async (res: Response, fallback: string): Promise<MemberCsError> => {
  let env: ErrorEnvelope | null = null;
  try {
    env = (await res.json()) as ErrorEnvelope;
  } catch {
    env = null;
  }
  const fieldErrors: Record<string, string> = {};
  for (const [k, v] of Object.entries(env?.error?.details ?? {})) {
    if (typeof v === 'string') fieldErrors[k] = v;
    else if (Array.isArray(v)) fieldErrors[k] = v.map(String).join(' ');
  }
  return new MemberCsError(env?.error?.message || fallback, res.status, env?.error?.code ?? null, fieldErrors);
};

// ───────────────────────── 1. 회원 정보 수정 ─────────────────────────

/** 보낸 필드만 바뀐다(null/미전송 = 유지, 지우기 없음). reason 은 필수(감사로그 MEMBER_PROFILE_UPDATE). */
export interface MemberProfilePatch {
  name?: string;
  email?: string;
  /** 없으면 기존 국가코드 유지. */
  mobileCountryCode?: string;
  /** 하이픈 허용 — BE 가 숫자만 남겨 저장한다. */
  mobileNumber?: string;
  /** yyyy-MM-dd, 과거 날짜만. */
  birthDate?: string;
  reason: string;
}

export const useMemberProfileMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminMemberDetail, MemberCsError, { userId: string; patch: MemberProfilePatch }>({
    mutationFn: async ({ userId, patch }) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw await toMemberCsError(res, '회원 정보를 수정하지 못했습니다.');
      const env = (await res.json()) as { data: AdminMemberDetail };
      return env.data;
    },
    // 응답은 마스킹 적용본이라 화면은 상세를 다시 불러온다(원문은 기존처럼 unmask 조회).
    onSuccess: (_d, { userId }) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', userId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};

// ───────────────────────── 2. 비밀번호 재설정 인증번호 대행 발송 ─────────────────────────

export type PasswordResetChannel = 'PHONE' | 'EMAIL';

export interface PasswordResetCodeResult {
  channel: PasswordResetChannel;
  /** 마스킹된 발송 대상 — 예: 010-****-5678. */
  sentTo: string;
}

export const usePasswordResetCodeMutation = () =>
  useMutation<
    PasswordResetCodeResult,
    MemberCsError,
    { userId: string; channel: PasswordResetChannel | null; reason: string }
  >({
    mutationFn: async ({ userId, channel, reason }) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/password/reset-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // channel 이 없으면 BE 가 몰 기본(한국몰 PHONE / 해외몰 EMAIL)으로 보낸다.
        body: JSON.stringify({ ...(channel ? { channel } : {}), reason }),
      });
      if (!res.ok) throw await toMemberCsError(res, '비밀번호 재설정 인증번호를 보내지 못했습니다.');
      const env = (await res.json()) as { data: PasswordResetCodeResult };
      return env.data;
    },
  });

// ───────────────────────── 3. 관리자 비밀번호 직접 지정 (QA-394, be-user #37) ─────────────────────────

/**
 * PATCH /api/admin/v1/users/{id}/password — 관리자가 새 비밀번호를 정한다(인증이 안 되는 고객 CS 용).
 * BE 가 함께: 이관 회원 비밀번호 생성·강제변경 해제, 로그인 잠금 해제, 휴면 해제, 기존 세션 폐기, 변경 완료 메일,
 * 감사로그 MEMBER_PASSWORD_SET_BY_ADMIN. 오류: 400 USER_PASSWORD_POLICY_FAILED(규칙) · 409(활성·휴면 아님) · 404.
 * 비밀번호는 요청 본문에만 싣고 어디에도 남기지 않는다(쿼리키·로그 없음).
 */
export const useMemberPasswordSetMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminMemberDetail, MemberCsError, { userId: string; newPassword: string; reason: string }>({
    mutationFn: async ({ userId, newPassword, reason }) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword, reason }),
      });
      if (!res.ok) throw await toMemberCsError(res, '비밀번호를 변경하지 못했습니다.');
      const env = (await res.json()) as { data: AdminMemberDetail };
      return env.data;
    },
    // 휴면 해제·잠금 해제로 상태가 바뀔 수 있어 상세·목록을 다시 불러온다.
    onSuccess: (_d, { userId }) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', userId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};
